//#region src/agent-work-activity.ts
const GITHUB_REPOSITORY_URL_PATTERN = /github\.com(?::|\/)([a-z\d](?:[a-z\d_.-]*[a-z\d])?)\/([a-z\d](?:[a-z\d_.-]*[a-z\d])?)(?:\.git)?(?=$|[\s'"/])/iu;
const GH_REPOSITORY_CLONE_PATTERN = /^gh\s+repo\s+clone\s+['"]?([a-z\d](?:[a-z\d_.-]*[a-z\d])?)\/([a-z\d](?:[a-z\d_.-]*[a-z\d])?)(?:\.git)?(?=$|[\s'"])/iu;
const GIT_SUFFIX_PATTERN = /\.git$/iu;
const GIT_BRANCH_ATTACHED_MUTATION_OPTION_PATTERN = /^-[cCdDmM].+/u;
const SHELL_ASSIGNMENT_PATTERN = /^[A-Z_][A-Z\d_]*=/iu;
const SHELL_WHITESPACE_PATTERN = /\s/u;
const UNSUPPORTED_SHELL_SYNTAX_PATTERN = /#|`|\$\(|<<|[<>]\(|\|\||(?:^|[^&])&(?:$|[^&])/u;
const SHELL_QUOTES = /* @__PURE__ */ new Set([
	"'",
	"\"",
	"`"
]);
const SHELL_SEGMENT_SEPARATORS = /* @__PURE__ */ new Set([
	";",
	"\n",
	"|",
	"&"
]);
const SAFE_NEUTRAL_EXECUTABLES = /* @__PURE__ */ new Set([
	"cd",
	"echo",
	"printf"
]);
const CONNECTED_APP_WORK_KEYS = /* @__PURE__ */ new Set(["connected_app.action.execute", "connected_app.actions.read"]);
const TEST_EXECUTABLES = /* @__PURE__ */ new Set(["pytest", "vitest"]);
const QUALITY_EXECUTABLES = /* @__PURE__ */ new Set([
	"biome",
	"eslint",
	"prettier",
	"tsc"
]);
const BUILD_EXECUTABLES = /* @__PURE__ */ new Set(["cargo", "go"]);
const INSTALL_EXECUTABLES = /* @__PURE__ */ new Set([
	"cargo",
	"pip",
	"pip3"
]);
const SEARCH_EXECUTABLES = /* @__PURE__ */ new Set([
	"rg",
	"grep",
	"fd"
]);
const READ_EXECUTABLES = /* @__PURE__ */ new Set([
	"cat",
	"head",
	"less",
	"more",
	"sed",
	"tail"
]);
const REPOSITORY_CHECK_EXECUTABLES = /* @__PURE__ */ new Set([
	"ls",
	"pwd",
	"test",
	"["
]);
const PACKAGE_MANAGER_EXECUTABLES = /* @__PURE__ */ new Set([
	"npm",
	"pnpm",
	"yarn",
	"bun"
]);
const PACKAGE_MANAGER_OPTIONS_WITH_VALUE = /* @__PURE__ */ new Set([
	"-C",
	"--dir",
	"--filter",
	"--prefix"
]);
const GIT_OPTIONS_WITH_VALUE = /* @__PURE__ */ new Set([
	"-C",
	"-c",
	"--git-dir",
	"--work-tree"
]);
const FIND_EXECUTION_ACTIONS = /* @__PURE__ */ new Set([
	"-exec",
	"-execdir",
	"-ok",
	"-okdir"
]);
const FD_EXECUTION_ACTIONS = /* @__PURE__ */ new Set([
	"-x",
	"-X",
	"--exec",
	"--exec-batch"
]);
const GIT_BRANCH_MUTATION_OPTIONS = /* @__PURE__ */ new Set([
	"-c",
	"-C",
	"-d",
	"-D",
	"-m",
	"-M",
	"--copy",
	"--delete",
	"--edit-description",
	"--move",
	"--set-upstream-to",
	"--unset-upstream"
]);
const PIPX_INSTALL_COMMANDS = /* @__PURE__ */ new Set([
	"inject",
	"install",
	"reinstall",
	"reinstall-all",
	"upgrade",
	"upgrade-all"
]);
function resolveCommandAgentWorkActivity(command) {
	if (!command?.trim() || hasUnsupportedShellSyntax(command)) return null;
	let resolvedWork = null;
	for (const segment of splitShellSegments(command)) {
		if (hasUnquotedOutputRedirection(segment)) return null;
		const words = tokenizeShellWords(segment);
		if (isSafeNeutralCommand(words)) continue;
		const work = classifyCommandWords(words, segment);
		if (!work) return null;
		if (resolvedWork && !hasSameWorkMeaning(resolvedWork, work)) return null;
		resolvedWork = work;
	}
	return resolvedWork;
}
function isAgentWorkActivityPresentable(work) {
	return !CONNECTED_APP_WORK_KEYS.has(work.key) || work.subject?.kind === "connected_app";
}
function isLegacyAgentActivityWorkCompatible(work) {
	return work?.subject?.kind !== "connected_app";
}
function classifyCommandWords(words, commandSegment) {
	if (hasUnquotedOutputRedirection(commandSegment)) return null;
	const normalizedWords = removeExecutionPrefixes(words);
	const executable = basename(normalizedWords[0] ?? "").toLowerCase();
	const args = normalizedWords.slice(1);
	if (!executable) return null;
	if (executable === "command") {
		if (args[0] === "-v" || args[0] === "-V") return work("environment.check");
		return classifyCommandWords(args, commandSegment);
	}
	if (executable === "git") return classifyGitCommand(args, commandSegment);
	if (executable === "gh" && args[0] === "repo" && args[1] === "clone") return repositoryCloneWork(commandSegment);
	if (PACKAGE_MANAGER_EXECUTABLES.has(executable)) return classifyPackageManagerCommand(args);
	return classifyDirectCommand(executable, args);
}
function classifyDirectCommand(executable, args) {
	if (isTestCommand(executable, args[0])) return work("tests.run");
	if (QUALITY_EXECUTABLES.has(executable)) return work("quality.check");
	if (BUILD_EXECUTABLES.has(executable) && args[0] === "build") return work("build.run");
	if (INSTALL_EXECUTABLES.has(executable) && ["add", "install"].includes(args[0] ?? "") || executable === "pipx" && PIPX_INSTALL_COMMANDS.has(args[0] ?? "")) return work("dependencies.install");
	if (executable === "find") return classifyFindCommand(args);
	if (executable === "fd" && hasFdExecutionAction(args)) return null;
	if (SEARCH_EXECUTABLES.has(executable)) return work("source.search");
	if (executable === "sed" && isSedInPlace(args)) return work("source.update");
	if (READ_EXECUTABLES.has(executable)) return work("source.read");
	if (REPOSITORY_CHECK_EXECUTABLES.has(executable)) return work("repository.check");
	return null;
}
function classifyFindCommand(args) {
	if (args.includes("-delete")) return work("source.update");
	return args.some((argument) => FIND_EXECUTION_ACTIONS.has(argument)) ? null : work("source.search");
}
function isSedInPlace(args) {
	return args.some((argument) => argument === "-i" || argument.startsWith("-i") || argument === "--in-place" || argument.startsWith("--in-place="));
}
function isTestCommand(executable, firstArgument) {
	return TEST_EXECUTABLES.has(executable) || executable === "node" && firstArgument === "--test" || BUILD_EXECUTABLES.has(executable) && firstArgument === "test";
}
function classifyGitCommand(args, commandSegment) {
	const subcommand = readGitSubcommand(args);
	if (subcommand === "clone") return repositoryCloneWork(commandSegment);
	if ([
		"diff",
		"log",
		"show",
		"status"
	].includes(subcommand ?? "")) return work("repository.inspect");
	if (subcommand === "branch") return isReadOnlyGitBranchCommand(args) ? work("repository.check") : null;
	if (subcommand === "rev-parse") return work("repository.check");
	return null;
}
function isReadOnlyGitBranchCommand(args) {
	return argsAfterCommand(args, "branch").every((argument) => argument.startsWith("-") && !isGitBranchMutationOption(argument));
}
function isGitBranchMutationOption(argument) {
	const option = argument.split("=")[0] ?? argument;
	return GIT_BRANCH_MUTATION_OPTIONS.has(option) || GIT_BRANCH_ATTACHED_MUTATION_OPTION_PATTERN.test(argument);
}
function classifyPackageManagerCommand(args) {
	const command = readPackageManagerCommand(args);
	if (!command) return null;
	if (["add", "install"].includes(command)) return work("dependencies.install");
	if (command === "test" || command.startsWith("test:")) return work("tests.run");
	if ([
		"check",
		"lint",
		"typecheck"
	].includes(command)) return work("quality.check");
	if (command === "build") return work("build.run");
	if (command === "exec") return classifyCommandWords(argsAfterCommand(args, command), "");
	if (command === "run") {
		const script = argsAfterCommand(args, command)[0]?.toLowerCase();
		if (!script) return null;
		if (script === "test" || script.startsWith("test:")) return work("tests.run");
		if ([
			"check",
			"lint",
			"typecheck"
		].includes(script)) return work("quality.check");
		if (script === "build") return work("build.run");
		return null;
	}
	return null;
}
function readPackageManagerCommand(args) {
	return readFirstCommandWord(args, PACKAGE_MANAGER_OPTIONS_WITH_VALUE);
}
function argsAfterCommand(args, command) {
	const commandIndex = args.findIndex((value) => value.toLowerCase() === command);
	return commandIndex >= 0 ? args.slice(commandIndex + 1) : [];
}
function readGitSubcommand(args) {
	return readFirstCommandWord(args, GIT_OPTIONS_WITH_VALUE);
}
function readFirstCommandWord(words, optionsWithValue) {
	let skipNext = false;
	for (const value of words) {
		if (skipNext) {
			skipNext = false;
			continue;
		}
		if (optionsWithValue.has(value)) {
			skipNext = true;
			continue;
		}
		if (value && !value.startsWith("-")) return value.toLowerCase();
	}
	return null;
}
function removeExecutionPrefixes(words) {
	let index = 0;
	while (SHELL_ASSIGNMENT_PATTERN.test(words[index] ?? "")) index += 1;
	if (words[index] === "sudo") {
		index += 1;
		while ((words[index] ?? "").startsWith("-")) index += 1;
	}
	if (words[index] === "env") {
		index += 1;
		while ((words[index] ?? "").startsWith("-") || SHELL_ASSIGNMENT_PATTERN.test(words[index] ?? "")) index += 1;
	}
	if (words[index] === "corepack") index += 1;
	return words.slice(index);
}
function isSafeNeutralCommand(words) {
	const normalizedWords = removeExecutionPrefixes(words);
	return SAFE_NEUTRAL_EXECUTABLES.has(basename(normalizedWords[0] ?? "").toLowerCase());
}
function hasSameWorkMeaning(left, right) {
	return left.key === right.key && left.subject?.kind === right.subject?.kind && left.subject?.label === right.subject?.label;
}
function hasUnsupportedShellSyntax(command) {
	return UNSUPPORTED_SHELL_SYNTAX_PATTERN.test(command);
}
function splitShellSegments(command) {
	const segments = [];
	let current = "";
	let quote = null;
	let escaped = false;
	for (let index = 0; index < command.length; index += 1) {
		const character = command[index] ?? "";
		if (escaped) {
			current += character;
			escaped = false;
			continue;
		}
		if (character === "\\") {
			current += character;
			escaped = true;
			continue;
		}
		if (quote) {
			current += character;
			if (character === quote) quote = null;
			continue;
		}
		if (SHELL_QUOTES.has(character)) {
			current += character;
			quote = character;
			continue;
		}
		const next = command[index + 1] ?? "";
		if (SHELL_SEGMENT_SEPARATORS.has(character)) {
			pushShellSegment(segments, current);
			current = "";
			if (isRepeatedShellSeparator(character, next)) index += 1;
			continue;
		}
		current += character;
	}
	pushShellSegment(segments, current);
	return segments;
}
function pushShellSegment(segments, segment) {
	const trimmed = segment.trim();
	if (trimmed) segments.push(trimmed);
}
function isRepeatedShellSeparator(character, next) {
	return (character === "|" || character === "&") && next === character;
}
function hasUnquotedOutputRedirection(command) {
	let quote = null;
	let escaped = false;
	for (const character of command) {
		if (escaped) {
			escaped = false;
			continue;
		}
		if (character === "\\") {
			escaped = true;
			continue;
		}
		if (quote) {
			if (character === quote) quote = null;
			continue;
		}
		if (SHELL_QUOTES.has(character)) {
			quote = character;
			continue;
		}
		if (character === ">") return true;
	}
	return false;
}
function hasFdExecutionAction(args) {
	return args.some((argument) => {
		const option = argument.split("=")[0] ?? argument;
		return FD_EXECUTION_ACTIONS.has(option);
	});
}
function tokenizeShellWords(segment) {
	const words = [];
	let current = "";
	let quote = null;
	let escaped = false;
	const pushCurrent = () => {
		if (current) {
			words.push(current);
			current = "";
		}
	};
	for (const character of segment) {
		if (escaped) {
			current += character;
			escaped = false;
			continue;
		}
		if (character === "\\") {
			escaped = true;
			continue;
		}
		if (quote) {
			if (character === quote) quote = null;
			else current += character;
			continue;
		}
		if (SHELL_QUOTES.has(character)) {
			quote = character;
			continue;
		}
		if (SHELL_WHITESPACE_PATTERN.test(character)) {
			pushCurrent();
			continue;
		}
		current += character;
	}
	pushCurrent();
	return words;
}
function repositoryCloneWork(command) {
	const repository = readGitHubRepository(command);
	return {
		key: "repository.clone",
		v: 1,
		...repository ? { subject: {
			kind: "repository",
			label: repository
		} } : {}
	};
}
function readGitHubRepository(command) {
	const githubUrlMatch = GITHUB_REPOSITORY_URL_PATTERN.exec(command);
	if (githubUrlMatch) return `${githubUrlMatch[1]}/${githubUrlMatch[2]?.replace(GIT_SUFFIX_PATTERN, "")}`;
	const ghCloneMatch = GH_REPOSITORY_CLONE_PATTERN.exec(command.trim());
	return ghCloneMatch ? `${ghCloneMatch[1]}/${ghCloneMatch[2]?.replace(GIT_SUFFIX_PATTERN, "")}` : null;
}
function basename(value) {
	return value.split("/").at(-1) ?? value;
}
function work(key) {
	return {
		key,
		v: 1
	};
}
//#endregion
export { isAgentWorkActivityPresentable, isLegacyAgentActivityWorkCompatible, resolveCommandAgentWorkActivity };

//# sourceMappingURL=agent-work-activity.js.map